/**
 * @author Jorge Casas
 * @creationDate 23/07/2024
 *
 * @description This component is used to display the codes of an asset.
 * It allows the user to download the QR code and the barcode of the asset, as well regenerate them.
 *
 * @version 3.4.14
 */

import React, { useState, useEffect } from 'react';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { WithoutQRCode } from '../../../assets/img/base64/without_qr_code.js';
import { Form } from 'common/components';
import { formatDateUTCWithTime } from '../../../utils/additional-functions/format-date.js';
import {
  getQRCodeAssetById,
  putRegenerateCodesByAssetId,
} from '../../../containers/assets/actions.js';
import { addGrowlError } from 'messages/actions';
import { downloadImageFromUrl } from 'lib/images/downloadImage';

const AssetsFormCodes = ({ values, t, addGrowlError }) => {
  const [qrcode, setQrcode] = useState('');
  const [barcode, setBarcode] = useState('');
  const [qrDataAsset, setQrDataAsset] = useState('');
  const [qrAssetName, setQrAssetName] = useState('');
  const [selectedOption, setSelectedOption] = useState('qr');

  const download = (e) => {
    e.preventDefault();
    // currentTarget, not target: clicking the <i> icon made target the icon,
    // whose href is undefined.
    const path = e.currentTarget.href;
    downloadImageFromUrl(path, 'image.png').catch((err) => {
      console.log('Error en la descarga:', err);
      addGrowlError('error.download_image');
    });
  };

  const getQRCodeAsset = async (assetId) => {
    try {
      const response = await getQRCodeAssetById(assetId);
      const data = await response.payload;

      if (data.qrcode) {
        setQrcode(data.qrcode.qrcode);
      }
    } catch (err) {
      console.log('Error en el fetch:', err);
    }
  };

  const regenerateCodes = async (companyId, subcompanyId, assetId) => {
    try {
      const response = await putRegenerateCodesByAssetId(companyId, subcompanyId, assetId);
      const data = await response.payload;

      console.log('Codes regenerated:', data);

      if (data.qrUrl) setQrcode(data.qrUrl);
      if (data.barcodeUrl) setBarcode(data.barcodeUrl);
      if (data.qrDataAssetUrl) setQrDataAsset(data.qrDataAssetUrl);
      if (data.qrAssetNameUrl) setQrAssetName(data.qrAssetNameUrl);

      window.location.reload();
    } catch (err) {
      console.log('Error en la regeneración de códigos:', err);
    }
  };

  useEffect(() => {
    if (values.id) {
      getQRCodeAsset(values.id);
    }
  }, [values.id]);

  const accountKey = values._account && values._account.key ? values._account.key : '';
  const subaccountKey = values._subaccount && values._subaccount.key ? values._subaccount.key : '';
  const number = values.number || '';

  let assetBarcodeData = '';

  if (accountKey) {
    assetBarcodeData = accountKey;
  }

  if (subaccountKey) {
    assetBarcodeData += assetBarcodeData ? `-${subaccountKey}` : subaccountKey;
  }

  if (number) {
    assetBarcodeData += assetBarcodeData ? `-${number}` : number;
  }

  const renderCodeImage = () => {
    switch (selectedOption) {
      case 'qr':
        return (
          <div className="text-center">
            <a href={values.qrUrl} target="__brank" rel="noopener noreferrer">
              <img src={values.qrUrl} style={{ width: '85%', maxWidth: '150px' }} alt="QR Code" />
            </a>
            <p>{qrcode}</p>
            <a href={values.qrUrl} download onClick={(e) => download(e)}>
              <i className="fa fa-download" />
              {' ' + t('assets.section.downloadQR')}
            </a>
          </div>
        );
      case 'barcode':
        return (
          <div className="text-center">
            <a href={values.barcodeUrl} target="__brank" rel="noopener noreferrer">
              <img
                src={values.barcodeUrl}
                style={{ width: '80%', maxWidth: '700px' }}
                alt="Barcode"
              />
            </a>
            <br />
            <p>
              {accountKey}
              {subaccountKey ? `${accountKey ? '-' : ''}${subaccountKey}` : ''}
              {number ? `${accountKey || subaccountKey ? '-' : ''}${number}` : ''}
            </p>
            <a href={values.barcodeUrl} download onClick={(e) => download(e)}>
              <i className="fa fa-download" />
              {' ' + t('assets.section.downloadBarcode')}
            </a>
          </div>
        );
      case 'qrDataAsset':
        return (
          <div className="text-center">
            {values.qrDataAssetUrl ? (
              <React.Fragment>
                <a href={values.qrDataAssetUrl} target="__blank" rel="noopener noreferrer">
                  <img
                    src={values.qrDataAssetUrl || WithoutQRCode}
                    style={{ width: '85%', maxWidth: '150px' }}
                    alt="QR Code"
                  />
                </a>
                <br />
              </React.Fragment>
            ) : (
              <img
                src={WithoutQRCode}
                style={{ width: '85%', maxWidth: '150px' }}
                alt="QR Code Not Available"
              />
            )}
            <p>
              {accountKey}
              {subaccountKey ? `${accountKey ? '-' : ''}${subaccountKey}` : ''}
              {number ? `${accountKey || subaccountKey ? '-' : ''}${number}` : ''}
            </p>
            {values.qrDataAssetUrl && (
              <a href={values.qrDataAssetUrl} download onClick={(e) => download(e)}>
                <i className="fa fa-download" />
                {' ' + t('assets.section.downloadQR')}
              </a>
            )}
          </div>
        );
      case 'qrAssetName':
        return (
          <div className="text-center">
            {values.qrAssetNameUrl ? (
              <React.Fragment>
                <a href={values.qrAssetNameUrl} target="__blank" rel="noopener noreferrer">
                  <img
                    src={values.qrAssetNameUrl || WithoutQRCode}
                    style={{ width: '85%', maxWidth: '150px' }}
                    alt="QR Code"
                  />
                </a>
                <br />
              </React.Fragment>
            ) : (
              <img
                src={WithoutQRCode}
                style={{ width: '85%', maxWidth: '150px' }}
                alt="QR Code Not Available"
              />
            )}
            <p>
              {accountKey}
              {subaccountKey ? `${accountKey ? '-' : ''}${subaccountKey}` : ''}
              {number ? `${accountKey || subaccountKey ? '-' : ''}${number}` : ''}
            </p>
            {values.qrAssetNameUrl && (
              <a href={values.qrAssetNameUrl} download onClick={(e) => download(e)}>
                <i className="fa fa-download" />
                {' ' + t('assets.section.downloadQR')}
              </a>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (selectedOption) {
      case 'qr':
        return t('assets.section.codes.title_qrUrl');
      case 'barcode':
        return t('assets.section.codes.title_barcode');
      case 'qrDataAsset':
        return t('assets.section.codes.title_qrData');
      case 'qrAssetName':
        return t('assets.section.codes.title_qrName');
      default:
        return t('assets.section.codes.title');
    }
  };

  const getInfoCodes = () => {
    switch (selectedOption) {
      case 'qr':
        return `${t('assets.section.codes.info.qrUrl')}`;
      case 'barcode':
        return `${t('assets.section.codes.info.barcode')}`;
      case 'qrDataAsset':
        return `${t('assets.section.codes.info.qrData')}`;
      case 'qrAssetName':
        return `${t('assets.section.codes.info.qrName')}`;
      default:
        return t('assets.section.codes.info');
    }
  };

  const getDestinationCodes = () => {
    switch (selectedOption) {
      case 'qr':
        return `https://activofijosaaf.com/QR/${qrcode}`;
      case 'barcode':
        return `${accountKey}
          ${subaccountKey ? `${accountKey ? '-' : ''}${subaccountKey}` : ''}
          ${number ? `${accountKey || subaccountKey ? '-' : ''}${number}` : ''}`;
      case 'qrDataAsset':
        return `${t('assets.section.codes.fields_data_asset.name')}: ${values.name}\n${t(
          'assets.section.codes.fields_data_asset.number'
        )}: ${values.number}\n${t('assets.section.codes.fields_data_asset.brand')}: ${
          values.brand ? values.brand : ' - '
        }\n${t('assets.section.codes.fields_data_asset.model')}: ${
          values.model ? values.model : ' - '
        }\n${t('assets.section.codes.fields_data_asset.serie')}: ${
          values.serie ? values.serie : ' - '
        }\n${t('assets.section.codes.fields_data_asset.createdAt')}: ${formatDateUTCWithTime(
          values.createdAt
        )}\n${t('assets.section.codes.fields_data_asset.updatedAt')}: ${formatDateUTCWithTime(
          values.updatedAt
        )}`;
      case 'qrAssetName':
        return `${accountKey}
              ${subaccountKey ? `${accountKey ? '-' : ''}${subaccountKey}` : ''}
              ${number ? `${accountKey || subaccountKey ? '-' : ''}${number}` : ''}`;
      default:
        return t('assets.section.codes.info');
    }
  };

  const formatText = (text) => text.replace(/\n/g, '<br>');

  const displayText =
    selectedOption === 'qrDataAsset' ? formatText(getDestinationCodes()) : getDestinationCodes();

  return (
    <Form.Section>
      <div className="row">
        <div className="col-12 d-flex justify-content-center my-4">
          <button
            type="button"
            className="btn btn-outline-secondary mx-2"
            onClick={() => setSelectedOption('qr')}
          >
            {t('actions.qr_url')}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary mx-2"
            onClick={() => setSelectedOption('qrAssetName')}
          >
            {t('actions.qr_name')}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary mx-2"
            onClick={() => setSelectedOption('qrDataAsset')}
          >
            {t('actions.qr_info')}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary mx-2"
            onClick={() => setSelectedOption('barcode')}
          >
            {t('actions.barcode')}
          </button>
          <button
            type="button"
            className="btn btn-info mx-2"
            onClick={() => regenerateCodes(values._company, values._subcompany, values.id)}
          >
            {t('actions.regenerateCodes')}
          </button>
        </div>
      </div>
      <div className="card mx-auto" style={{ maxWidth: '600px' }}>
        <div className="card-body d-flex justify-content-between align-items-center">
          <div>
            <h5 className="card-title" style={{ marginBottom: '1rem' }}>
              {getTitle()}
            </h5>
            <p className="card-text" style={{ marginTop: '1rem' }}>
              {getInfoCodes()}
            </p>
            <div
              dangerouslySetInnerHTML={{ __html: displayText }}
              style={{ marginBottom: '1rem' }}
            />
          </div>
          {renderCodeImage()}
        </div>
      </div>
    </Form.Section>
  );
};

export default connect(null, { addGrowlError })(translate()(AssetsFormCodes));



// WEBPACK FOOTER //
// src/components/assets/form_sections/AssetFormCodes.js